/**
 * SearchPanel — Suchparameter + Zuweisungsregel. Mobil nach einer Suche zu einer
 * Zusammenfassungszeile eingeklappt (kein horizontales Scrollen, kein Überlagern).
 */
import { useState } from 'react';
import { Loader2, MapPin, Search, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_SMALL, CARD, LABEL, SELECT } from '@/components/ausgaben/dna';
import { DISPLAY_BRANCHES } from '@/lib/leadHunter/branches';
import { cn } from '@/lib/utils';
import AssignmentRules from './AssignmentRules';

const RADII = [2, 5, 10, 25];

export default function SearchPanel({ hunter, hasResults }) {
  const [branch, setBranch] = useState('Burger');
  const [location, setLocation] = useState('München');
  const [radiusKm, setRadiusKm] = useState(5);
  const [expanded, setExpanded] = useState(false);
  const { agents, stats, rules, selected, isSearching } = hunter;
  const collapsed = hasResults && !expanded;

  const submit = (e) => {
    e.preventDefault();
    setExpanded(false);
    hunter.search({ branch, location, radiusKm });
  };

  return (
    <section className={cn(CARD, 'lg:sticky lg:top-24')} aria-label="Suchparameter">
      {/* Mobil: eingeklappte Zusammenfassung nach der Suche */}
      <div className={cn('flex items-center gap-3 px-4 py-3 lg:hidden', !collapsed && 'hidden')}>
        <MapPin className="h-4 w-4 shrink-0 text-dna-muted" />
        <p className="min-w-0 flex-1 truncate text-[14px] text-dna-text">
          <span className="font-semibold text-dna-primary">{branch}</span> · {location} · {radiusKm} km
        </p>
        <Button type="button" variant="ghost" className={BTN_SMALL} onClick={() => setExpanded(true)}>
          <SlidersHorizontal className="h-4 w-4" /> Ändern
        </Button>
      </div>

      <form onSubmit={submit} className={cn('space-y-4 p-4 sm:p-5', collapsed && 'hidden lg:block')}>
        <h2 className="text-[17px] font-semibold text-dna-primary">Suche</h2>
        <label className="block space-y-1.5">
          <span className={LABEL}>Branche</span>
          <select value={branch} onChange={(e) => setBranch(e.target.value)} className={SELECT}>
            {DISPLAY_BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label className="block space-y-1.5">
          <span className={LABEL}>Ort</span>
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="z. B. München Schwabing"
            className="h-11 w-full rounded-md border border-dna-field bg-white px-3 text-[15px] text-dna-text placeholder:text-dna-placeholder focus:outline-none focus:ring-2 focus:ring-dna-focus" />
        </label>
        <div className="space-y-1.5">
          <span className={LABEL} id="radius-label">Umkreis</span>
          <div className="grid grid-cols-4 gap-1 rounded-lg bg-dna-warm p-1" role="radiogroup" aria-labelledby="radius-label">
            {RADII.map((r) => (
              <button key={r} type="button" role="radio" aria-checked={radiusKm === r} onClick={() => setRadiusKm(r)}
                className={cn('h-10 rounded-md text-[14px] font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus',
                  radiusKm === r ? 'bg-white text-dna-primary shadow-[0_1px_2px_rgba(34,34,67,0.06)]' : 'text-dna-muted hover:text-dna-primary')}>
                {r} km
              </button>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={isSearching} className={cn(hasResults ? BTN_SECONDARY : BTN_PRIMARY, 'w-full')} variant={hasResults ? 'ghost' : 'default'}>
          {isSearching ? <><Loader2 className="h-4 w-4 animate-spin" /> Suche läuft…</> : <><Search className="h-4 w-4" /> {hasResults ? 'Neu suchen' : 'Leads suchen'}</>}
        </Button>

        <div className="border-t border-dna-line pt-4">
          <AssignmentRules agents={agents} stats={stats} {...rules}
            canApply={selected.size > 0 && (rules.rule !== 'fest' || !!rules.fixedAgent)} />
        </div>
      </form>
    </section>
  );
}
